import { useMemo, useState } from 'react';
import { CheckCircle, Loader2, Ticket, Timer } from 'lucide-react';
import { submitContactForm } from '../api/contact';
import { formatCountdown, getOfferRemainingMs, getOrCreateOfferExpiryIso, OFFER_CODE } from '../utils/offerCampaign';

const DEFAULT_SUBJECT = 'Demo Basvurusu';

export default function ApplicationSection() {
  const [formState, setFormState] = useState({
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    note: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const offerExpiryIso = useMemo(() => getOrCreateOfferExpiryIso(), []);
  const offerCountdown = useMemo(() => formatCountdown(getOfferRemainingMs(offerExpiryIso)), [offerExpiryIso]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const messageParts = [
        `Firma: ${formState.companyName.trim() || '-'}`,
        `Not: ${formState.note.trim() || '-'}`,
        `Kampanya Kodu: ${OFFER_CODE}`,
      ];

      await submitContactForm({
        fullName: formState.fullName.trim(),
        email: formState.email.trim(),
        phone: formState.phone.trim() || null,
        subject: DEFAULT_SUBJECT,
        message: messageParts.join('\n'),
        sourcePage: typeof window !== 'undefined' ? `${window.location.pathname || '/'}#basvuru` : '/#basvuru',
        submissionType: 'basvuru',
        discountCode: OFFER_CODE,
        offerExpiresAtUtc: offerExpiryIso,
      });

      setIsSubmitted(true);
      setFormState({
        fullName: '',
        email: '',
        phone: '',
        companyName: '',
        note: '',
      });
      window.setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Basvuru gonderilirken bir hata olustu.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="basvuru" className="section-padding bg-slate-100/60 dark:bg-slate-900/40 border-y border-slate-200 dark:border-slate-800">
      <div className="container-custom">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
              Basvuru Formu
            </span>
            <h2 className="text-3xl md:text-4xl font-black mb-4 text-slate-900 dark:text-white">
              Hemen Ucretsiz Basla
            </h2>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
              Kisa formu doldur, ekibimiz demo planlamasi icin seninle iletisime gecsin.
              Basvurun admin paneline <strong>basvuru</strong> tipi ile kaydedilir.
            </p>

            <div className="space-y-3">
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 flex items-center gap-3">
                <Ticket className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Indirim Kodu</p>
                  <p className="font-black text-primary">{OFFER_CODE}</p>
                </div>
              </div>
              <div className="rounded-xl border border-amber-300/60 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/20 p-4 flex items-center gap-3">
                <Timer className="w-5 h-5 text-amber-600 dark:text-amber-300" />
                <div>
                  <p className="text-xs text-amber-700 dark:text-amber-300">Teklif suresi</p>
                  <p className="font-black text-amber-700 dark:text-amber-300">{offerCountdown}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6">
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-3">
                  <CheckCircle className="w-7 h-7 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="font-bold text-slate-900 dark:text-white">Basvurun alindi</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                  Ekip kisa surede seninle iletisime gececek.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {errorMessage ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
                    {errorMessage}
                  </div>
                ) : null}

                <input
                  type="text"
                  name="fullName"
                  value={formState.fullName}
                  onChange={handleChange}
                  placeholder="Ad Soyad"
                  maxLength={120}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-700"
                />
                <input
                  type="email"
                  name="email"
                  value={formState.email}
                  onChange={handleChange}
                  placeholder="E-posta"
                  maxLength={120}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-700"
                />
                <input
                  type="tel"
                  name="phone"
                  value={formState.phone}
                  onChange={handleChange}
                  placeholder="Telefon"
                  maxLength={32}
                  className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-700"
                />
                <input
                  type="text"
                  name="companyName"
                  value={formState.companyName}
                  onChange={handleChange}
                  placeholder="Firma / Ofis"
                  maxLength={160}
                  className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-700"
                />
                <textarea
                  name="note"
                  value={formState.note}
                  onChange={handleChange}
                  rows={4}
                  maxLength={1200}
                  placeholder="Kisa not (opsiyonel)"
                  className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-700 resize-none"
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-xl bg-primary text-white font-bold py-3.5 hover:brightness-105 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Gonderiliyor...
                    </span>
                  ) : (
                    'Basvuru Gonder'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
